import { Link } from "react-router-dom";
import { ShieldStar, ArrowRight, CheckCircle, Globe, Lightning, Scales, Eye, Cube } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F7F7F8] text-[#0A0B0D]" data-testid="landing-page">
      {/* ---- Top nav ---- */}
      <nav className="border-b border-[#E5E5EA] bg-white sticky top-0 z-30">
        <div className="max-w-[1320px] mx-auto h-14 px-5 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-home">
            <img src="/api/static/brand/C2_Horizontal_White.svg" alt="SPAN CasePrime" style={{ width: "220px", height: "auto" }} />
          </Link>
          <div className="flex items-center gap-1 sm:gap-4 text-[12px]">
            <Link to="/pricing" className="px-3 py-2 hover:text-[#0A0B0D]/70" data-testid="nav-pricing">Pricing</Link>
            <Link to="/health-score" className="px-3 py-2 hover:text-[#0A0B0D]/70" data-testid="nav-health-score">Health Score</Link>
            <Link to="/login" className="px-3 py-2 hover:text-[#0A0B0D]/70" data-testid="nav-login">Sign in</Link>
            <Link to="/health-score" className="span-btn ml-1" data-testid="nav-cta">
              Start free <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <section className="relative overflow-hidden border-b border-[#E5E5EA]">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
             style={{backgroundImage: "repeating-linear-gradient(0deg, #000 0, #000 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, #000 0, #000 1px, transparent 1px, transparent 40px)"}} />
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="label-eyebrow mb-5">SPAN FRAMEWORK™ · ISO 27035</div>
              <h1 className="font-display font-bold text-[44px] sm:text-[64px] lg:text-[80px] leading-[0.95] tracking-[-0.03em]">
                Court-admissible<br/>
                <span className="inline-block bg-[#0A0B0D] text-white px-3 -mx-1">brand protection</span><br/>
                for global counsel.
              </h1>
              <p className="text-[17px] lg:text-[19px] text-[#475467] mt-7 max-w-[600px] leading-[1.55]">
                SPAN gives in-house counsel and brand protection teams a unified command surface
                across cases, evidence, OSINT, finance and field operations — battle-tested across
                <b className="text-[#0A0B0D]"> 13 case codes</b>, <b className="text-[#0A0B0D]">9 currencies</b>, and <b className="text-[#0A0B0D]">9 RBAC departments</b>.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-9">
                <Link to="/health-score" className="span-btn" data-testid="hero-cta-primary">
                  Get your free Health Score <ArrowRight size={14} />
                </Link>
                <Link to="/pricing" className="span-btn span-btn-ghost" data-testid="hero-cta-secondary">
                  See pricing
                </Link>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 text-[11px] font-mono uppercase tracking-[0.18em] text-[#475467]">
                <span>· No credit card</span>
                <span>· 14-day Pro trial</span>
                <span>· Cancel anytime</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border-2 border-black bg-white p-6 lg:p-7 shadow-[8px_8px_0_0_#0A0B0D]">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">CASE FILE</div>
                  <div className="font-mono text-[11px] text-[#dc2626]">● LIVE</div>
                </div>
                <div className="font-mono text-[12px] mt-3 text-[#475467]">PHA-IN-2026-014</div>
                <div className="font-display font-bold text-[20px] leading-tight mt-1">Counterfeit Cardiothrust seizure · Lucknow</div>
                <div className="text-[13px] text-[#475467] mt-2">Field officer dispatched · 47 units recovered · UP-FDA briefed.</div>
                <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-[#E5E5EA]">
                  {[
                    {k: "EVIDENCE", v: "12 items"},
                    {k: "VALUE", v: "$94,200"},
                    {k: "CHAIN", v: "ANCHORED"},
                  ].map((s) => (
                    <div key={s.k}>
                      <div className="label-eyebrow text-[9px]">{s.k}</div>
                      <div className="font-display font-bold text-[15px] mt-1 tnum">{s.v}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-5 pt-5 border-t border-[#E5E5EA]">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#475467]">Chain of custody locked · SHA-256 · HMAC-anchored</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Logos strip ---- */}
      <section className="border-b border-[#E5E5EA] py-9">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="label-eyebrow text-center mb-5">Trusted across consumer, pharma, auto & financial brands</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[#475467]/70">
            <span>BAJAJ AUTO</span><span>·</span>
            <span>MARICO LTD</span><span>·</span>
            <span>GSK PHARMA</span><span>·</span>
            <span>BRITANNIA</span><span>·</span>
            <span>ASIAN PAINTS</span><span>·</span>
            <span>HDFC ERGO</span>
          </div>
        </div>
      </section>

      {/* ---- Feature grid (6 modules) ---- */}
      <section className="border-b border-[#E5E5EA] py-20">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-5">
              <div className="label-eyebrow mb-3">THE STACK</div>
              <h2 className="font-display font-bold text-[36px] lg:text-[48px] leading-[1.02] tracking-tight">
                Six modules.<br/>One source of truth.
              </h2>
            </div>
            <div className="lg:col-span-7 self-end">
              <p className="text-[16px] text-[#475467] leading-[1.6]">
                Every counterfeit raid, dual-currency invoice, OSINT signal, field officer action
                and CEO escalation lives in the same operational graph — fully auditable, fully exportable.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5EA] border border-[#E5E5EA]">
            {[
              {icon: ShieldStar, t: "Case Management", d: "13 case codes, RBAC-aware workflows, dual-track activity log.", k: "01"},
              {icon: Scales,     t: "Finance",         d: "9-currency dual invoicing, 5-stage payables kanban, FX-aware.", k: "02"},
              {icon: Lightning,  t: "Escalations",     d: "Auto-trigger CEO queue on >$10K or sensitive codes. Live SLA.", k: "03"},
              {icon: Cube,       t: "Evidence",        d: "Chain of custody, SHA-256 + HMAC anchors, court-bundle PDFs.", k: "04"},
              {icon: Eye,        t: "Intelligence",    d: "IntelliProbe v3.0 OSINT, Claude AI signals, gov-feed bridges.", k: "05"},
              {icon: Globe,      t: "Field Ops",       d: "Officer dispatch, mobile evidence upload, RTL geo-routing.", k: "06"},
            ].map((f) => (
              <div key={f.k} className="bg-white p-7 hover:bg-[#FAFAFB] transition-colors group">
                <div className="flex items-start justify-between">
                  <f.icon size={26} weight="duotone" />
                  <div className="font-mono text-[10px] text-[#475467]">{f.k}</div>
                </div>
                <div className="font-display font-bold text-[18px] mt-5">{f.t}</div>
                <div className="text-[13px] text-[#475467] mt-2 leading-[1.55]">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Stat strip ---- */}
      <section className="border-b border-[#E5E5EA] py-20 bg-[#0A0B0D] text-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              {n: "13",   l: "case codes covered"},
              {n: "9",    l: "currencies · daily FX"},
              {n: "ISO 27035", l: "evidence chain standard"},
              {n: "<24h", l: "average response time"},
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display font-bold text-[44px] lg:text-[64px] leading-none tracking-tight tnum">{s.n}</div>
                <div className="label-eyebrow mt-3 text-white/60" style={{color: "rgba(255,255,255,0.5)"}}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Health Score CTA ---- */}
      <section className="border-b border-[#E5E5EA] py-20">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="label-eyebrow mb-3">FREE · NO LOGIN REQUIRED</div>
              <h2 className="font-display font-bold text-[36px] lg:text-[48px] leading-[1.02] tracking-tight">
                Score your brand's<br/>exposure in 60 seconds.
              </h2>
              <p className="text-[16px] text-[#475467] mt-5 max-w-[560px] leading-[1.6]">
                SPAN IntelliProbe scans marketplace listings, social impersonation,
                dark-web exposure and domain squatters — and gives you a single
                exposure index with prioritised next steps.
              </p>
              <Link to="/health-score" className="span-btn mt-7 inline-flex" data-testid="health-score-cta">
                Run the Health Score <ArrowRight size={14} />
              </Link>
            </div>
            <div className="lg:col-span-5 border-2 border-black bg-white p-7">
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">SAMPLE REPORT</div>
                <div className="font-mono text-[10px] text-[#dc2626]">CRITICAL</div>
              </div>
              <div className="font-display font-bold text-[58px] tnum leading-none mt-3">58.2</div>
              <div className="text-[12px] text-[#475467] mt-1">Marico Limited · FMCG · IN</div>
              <div className="space-y-2 mt-5 pt-5 border-t border-[#E5E5EA]">
                {[
                  ["Marketplace listings", 38],
                  ["Social impersonation", 42],
                  ["Domain squatting", 50],
                  ["Chain-of-custody readiness", 55],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-44 text-[12px] text-[#475467]">{k}</div>
                    <div className="flex-1 h-1.5 bg-[#E5E5EA] relative">
                      <div className="absolute inset-y-0 left-0 bg-[#0A0B0D]" style={{width: `${v}%`}} />
                    </div>
                    <div className="font-mono text-[11px] tnum w-7 text-right">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Final CTA ---- */}
      <section className="py-24 bg-[#F7F7F8]">
        <div className="max-w-[840px] mx-auto px-5 text-center">
          <div className="label-eyebrow mb-4">START YOUR 14-DAY TRIAL</div>
          <h2 className="font-display font-bold text-[40px] sm:text-[56px] leading-[0.98] tracking-tight">
            Open your first case in <span className="bg-[#0A0B0D] text-white px-2">24 hours.</span>
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <Link to="/health-score" className="span-btn" data-testid="footer-cta-primary">
              Get your free score <ArrowRight size={14} />
            </Link>
            <Link to="/pricing" className="span-btn span-btn-ghost" data-testid="footer-cta-secondary">
              Compare plans
            </Link>
          </div>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="border-t border-[#E5E5EA] py-10 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-[#0A0B0D] text-white font-display font-bold text-[10px] flex items-center justify-center">S</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#475467]">© SPAN CasePrime · A SPAN CONSULTING PRODUCT</div>
          </div>
          <div className="flex items-center gap-5 text-[12px] text-[#475467]">
            <Link to="/pricing">Pricing</Link>
            <Link to="/health-score">Health Score</Link>
            <Link to="/verify">Verify evidence</Link>
            <Link to="/counterfeit-registry">Counterfeit Registry</Link>
            <Link to="/state-of-brand">2026 Report</Link>
            <a
              data-testid="landing-user-guide"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/user-guide`}
              download="SPAN_User_Guide.docx"
              rel="noreferrer"
            >User guide (.docx)</a>
            <Link to="/login">Operator console</Link>
            <a href="mailto:pankaj@span.com">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
